import { useQuery } from 'react-query'

import {
  fetchManageFloor,
  fetchImage,
  fetchPointImages,
  fetchPoints,
} from '../../../../../api/buildingFetches'
import { Point, ProjectFloor } from '../../../../../api/types'
import { fetchViewpointFlatImages } from '../../../../../api/viewpointFlatImages'
import { createObjectURLSafe } from '../../../../../utils/imageUtils'

import {
  buildLastScanByPointId,
  formatScanHealthHoverLabel,
  getScanHealthColorClass,
} from './scanHealthUtils'

export interface ScanHealthPoint extends Point {
  severityClass: string
  lastScanDate: string | null
  hoverLabel: string
}

export interface ScanHealthFloorData {
  floorCode: string
  floorName: string
  floorOrder: number
  isNon360ImageFloor: boolean
  imageUrl?: string
  points: ScanHealthPoint[]
}

export const fetchScanHealthFloor = async (
  projectId: string,
  floor: ProjectFloor,
): Promise<ScanHealthFloorData> => {
  const floorDetail = await fetchManageFloor(projectId, floor.floor_code)
  const isNon360ImageFloor = !!(
    floorDetail?.is_non_360_image_floor ?? floor.is_non_360_image_floor
  )

  let imageUrl: string | undefined
  const planUrl = floorDetail?.latest_floor_plan?.web_image_url
  if (planUrl) {
    const img = await fetchImage(planUrl)
    imageUrl = createObjectURLSafe(img) ?? undefined
  }

  const rawPoints = (await fetchPoints(projectId, floor.floor_code, true)) ?? []

  const images =
    (await fetchPointImages(projectId, floor.floor_code, {
      fields: 'sub_point_id,taken_on',
    })) ?? []

  const flatImages = isNon360ImageFloor
    ? ((await fetchViewpointFlatImages(projectId, floor.floor_code)) ?? [])
    : []

  const lastScanByPointId = buildLastScanByPointId(images, flatImages)

  const points: ScanHealthPoint[] = rawPoints.map(
    (point: {
      point_id: number | string
      x: number | string
      y: number | string
    }) => {
      const pointId = String(point.point_id)
      const lastScanDate = lastScanByPointId[pointId] ?? null

      return {
        ...point,
        point_id: pointId,
        x: Number(point.x),
        y: Number(point.y),
        lastScanDate,
        severityClass: getScanHealthColorClass(lastScanDate),
        hoverLabel: formatScanHealthHoverLabel(lastScanDate),
        markerTitle: formatScanHealthHoverLabel(lastScanDate),
      }
    },
  )

  return {
    floorCode: floor.floor_code,
    floorName: floor.name,
    floorOrder: floor.order ?? 0,
    isNon360ImageFloor,
    imageUrl,
    points,
  }
}

export const useScanHealthFloorQuery = (
  projectId: string | undefined,
  floor: ProjectFloor,
  enabled: boolean,
) =>
  useQuery(
    ['scanHealth', projectId, floor.floor_code],
    () => fetchScanHealthFloor(projectId!, floor),
    {
      enabled: !!projectId && enabled,
      staleTime: 5 * 60 * 1000,
    },
  )
